import { useState } from "react";
import { differenceInCalendarDays, parse } from "date-fns";
import { Calendar, Download } from "lucide-react";
import { Button, Modal } from "@/ui/atoms";
import { MultiSelect } from "@/ui/molecules/MultiSelect";
import {
    presetLast12Months,
    presetLast30Days,
    presetLast3Months,
    presetLastMonth,
    presetThisMonth,
} from "@/utils/dateRangeFilter";

/** Mirrors the backend EXPORT_MAX_DAYS limit (inclusive span). */
export const AUDIT_EXPORT_MAX_DAYS = 366;

export type TAuditLogExportRequest = {
    dateFrom: string;
    dateTo: string;
    categories: string[];
};

export type TAuditLogExportModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onExport: (request: TAuditLogExportRequest) => Promise<void>;
    categoryOptions: readonly { value: string; label: string }[];
};

const PRESETS: { label: string; range: () => { from: string; to: string } }[] = [
    { label: "Last 30 days", range: presetLast30Days },
    { label: "This month", range: presetThisMonth },
    { label: "Last month", range: presetLastMonth },
    { label: "Last 3 months", range: presetLast3Months },
    { label: "Last 12 months", range: presetLast12Months },
];

const PRESET_BTN =
    "rounded-md border border-border bg-muted/30 px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground";

const INPUT_CLASS =
    "h-9 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50";

export function validateExportRange(from: string, to: string): string | null {
    if (!from || !to) return "Select a start and end date";
    const fromDay = parse(from, "yyyy-MM-dd", new Date());
    const toDay = parse(to, "yyyy-MM-dd", new Date());
    if (Number.isNaN(fromDay.getTime()) || Number.isNaN(toDay.getTime())) {
        return "Enter valid dates";
    }
    if (fromDay > toDay) return "Start date must be on or before end date";
    if (differenceInCalendarDays(toDay, fromDay) + 1 > AUDIT_EXPORT_MAX_DAYS) {
        return "Date range cannot exceed 12 months";
    }
    return null;
}

export function AuditLogExportModal({
    open,
    onOpenChange,
    onExport,
    categoryOptions,
}: TAuditLogExportModalProps) {
    const [range, setRange] = useState(presetLast30Days);
    const [categories, setCategories] = useState<string[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const validationError = validateExportRange(range.from, range.to);

    const updateRange = (next: { from: string; to: string }) => {
        setRange(next);
        setError(null);
    };

    const handleDownload = async () => {
        if (validationError) return;
        setIsDownloading(true);
        setError(null);
        try {
            await onExport({ dateFrom: range.from, dateTo: range.to, categories });
            onOpenChange(false);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Download failed");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Modal
            open={open}
            onOpenChange={(next) => {
                if (!isDownloading) onOpenChange(next);
            }}
            title="Download audit log"
            description="Exports every audit entry for your company in the selected range. Times are in CET."
        >
            <div className="space-y-4" data-testid="audit-log-export-modal">
                <div className="flex flex-wrap gap-1">
                    {PRESETS.map((preset) => (
                        <button
                            key={preset.label}
                            type="button"
                            className={PRESET_BTN}
                            onClick={() => updateRange(preset.range())}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <label className="space-y-1.5">
                        <span className="text-xs font-medium text-muted-foreground">From</span>
                        <div className="relative">
                            <Calendar className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <input
                                type="date"
                                value={range.from}
                                onChange={(e) => updateRange({ ...range, from: e.target.value })}
                                className={INPUT_CLASS}
                                aria-label="From date"
                            />
                        </div>
                    </label>
                    <label className="space-y-1.5">
                        <span className="text-xs font-medium text-muted-foreground">To</span>
                        <div className="relative">
                            <Calendar className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <input
                                type="date"
                                value={range.to}
                                onChange={(e) => updateRange({ ...range, to: e.target.value })}
                                className={INPUT_CLASS}
                                aria-label="To date"
                            />
                        </div>
                    </label>
                </div>

                <div className="space-y-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                        Categories (optional)
                    </span>
                    <MultiSelect
                        options={categoryOptions.map((o) => ({ id: o.value, label: o.label }))}
                        selectedIds={categories}
                        onChange={(ids) => {
                            setCategories(ids);
                            setError(null);
                        }}
                        placeholder="All categories"
                    />
                </div>

                {(validationError || error) && (
                    <p className="text-sm text-red-600" role="alert">
                        {error ?? validationError}
                    </p>
                )}

                <div className="flex justify-end gap-2 border-t border-border pt-3">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isDownloading}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid="audit-log-export-download"
                        onClick={() => void handleDownload()}
                        disabled={validationError !== null}
                        loading={isDownloading}
                    >
                        {!isDownloading && <Download className="h-4 w-4 mr-2" />}
                        Download
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
